import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ArrowRight } from "lucide-react";

export const Navigation = () => {
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const navLinks = [
    { path: "/", label: "Home" },
    { path: "/apps", label: "Apps" },
    { path: "/printables", label: "Printables" },
    { path: "/blog", label: "Blog" },
    { path: "/about", label: "About" },
    { path: "/contact", label: "Contact" },
  ];

  const isActive = (path) => location.pathname === path;

  // Close the mobile menu whenever the route changes
  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  // Lock body scroll while the mobile menu is open
  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [menuOpen]);

  return (
    <header
      className="fixed top-0 left-0 right-0 z-50 bg-[#F8F7FC] border-b border-[#E8E4F0]"
      data-testid="main-navigation"
    >
      {/* Logo band — shrinks on desktop after scrolling; mobile height unchanged */}
      <div
        className={`overflow-hidden flex items-center justify-center border-b border-[#E8E4F0]/50 bg-gradient-to-b from-[#F8F6F2] to-[#F5F2EB] transition-all duration-300 ease-out ${
          scrolled ? "h-24 md:h-16 lg:h-20" : "h-24 md:h-32 lg:h-40"
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
          <Link to="/" className="flex items-center justify-center" data-testid="nav-logo">
            <img
              src="https://customer-assets.emergentagent.com/job_everyday-works/artifacts/1fpywhgy_IMG_0729.jpeg"
              alt="Everyday Works — practical apps, planners and printables"
              className={`w-auto object-contain mix-blend-multiply transition-all duration-300 ease-out ${
                scrolled ? "h-40 md:h-28 lg:h-36" : "h-48 md:h-64 lg:h-80"
              }`}
              style={{ filter: "sepia(18%) saturate(85%) brightness(100%) contrast(95%)" }}
            />
          </Link>
        </div>
      </div>

      {/* Nav row */}
      <div className="py-2.5">
        <nav className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
          <div className="flex items-center justify-between gap-4">
            {/* Desktop links */}
            <ul className="hidden lg:flex items-center gap-7" data-testid="desktop-nav">
              {navLinks.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className={`text-sm font-medium tracking-wide transition-colors rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#B04E2E] focus-visible:ring-offset-2 focus-visible:ring-offset-[#F8F7FC] ${
                      isActive(link.path)
                        ? "text-[#2D2D3A]"
                        : "text-[#6B6B7A] hover:text-[#2D2D3A]"
                    }`}
                    data-testid={`nav-link-${link.label.toLowerCase()}`}
                  >
                    {link.label}
                    {isActive(link.path) && (
                      <span className="block h-0.5 mt-1 bg-[#B04E2E] rounded-full" />
                    )}
                  </Link>
                </li>
              ))}
            </ul>

            {/* Desktop CTA */}
            <Link
              to="/printables"
              className="hidden lg:inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#2D2D3A] text-white text-sm font-medium tracking-wide hover:bg-[#3D3D4A] transition-colors shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#B04E2E] focus-visible:ring-offset-2 focus-visible:ring-offset-[#F8F7FC]"
              data-testid="nav-browse-printables"
            >
              Browse Printables
              <ArrowRight className="w-4 h-4" />
            </Link>

            {/* Mobile hamburger */}
            <button
              type="button"
              onClick={() => setMenuOpen((v) => !v)}
              className="lg:hidden ml-auto inline-flex items-center justify-center w-11 h-11 rounded-full border border-[#E0DDE8] text-[#2D2D3A] hover:bg-white transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#B04E2E]"
              aria-label={menuOpen ? "Close menu" : "Open menu"}
              aria-expanded={menuOpen}
              data-testid="mobile-menu-button"
            >
              {menuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </nav>
      </div>

      {/* Mobile menu panel */}
      {menuOpen && (
        <>
          <button
            type="button"
            aria-label="Close menu"
            onClick={() => setMenuOpen(false)}
            className="lg:hidden fixed inset-0 top-0 bg-[#2D2D3A]/40 z-40"
            data-testid="mobile-menu-backdrop"
          />
          <div
            className="lg:hidden absolute left-0 right-0 top-full border-t border-[#E8E4F0] bg-[#F8F7FC] shadow-xl z-50"
            data-testid="mobile-menu"
          >
            <ul className="px-6 py-4 space-y-1">
              {navLinks.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className={`block px-3 py-3 rounded-lg text-base font-medium transition-colors ${
                      isActive(link.path)
                        ? "bg-white text-[#2D2D3A]"
                        : "text-[#5A5A6E] hover:bg-white hover:text-[#2D2D3A]"
                    }`}
                    data-testid={`mobile-nav-link-${link.label.toLowerCase()}`}
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
              <li className="pt-2">
                <Link
                  to="/printables"
                  className="flex items-center justify-center gap-2 px-4 py-3 rounded-full bg-[#2D2D3A] text-white text-sm font-medium hover:bg-[#3D3D4A] transition-colors"
                  data-testid="mobile-browse-printables"
                >
                  Browse Printables
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </li>
            </ul>
          </div>
        </>
      )}
    </header>
  );
};
